import { QueryClientProvider } from '@tanstack/react-query';
import { Component, lazy, Suspense, useState, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';

import { createQueryClient } from '@/api/queryClient';
import { AuthProvider, RequireRole, homePathForRole, useAuth } from '@/auth';
import { Login } from '@/pages/Login';
import { Button, EmptyState, Spinner, ToastProvider } from '@/ui';

/**
 * The shell: providers, routing, and the redirect that decides which of the two
 * interfaces a signed-in person belongs in.
 *
 * The two interfaces are lazy: a driver on a phone downloads the driver bundle
 * and never the admin tables, and vice versa. They own their own route folders
 * from here down — this file mounts them and does not know what is inside.
 */

const AdminApp = lazy(() => import('./admin'));
const DriverApp = lazy(() => import('./driver'));

/**
 * The root.
 *
 * An admin lands on /admin, a driver on /driver, and someone signed out on
 * /login. Rendering a chooser here would be one more tap between a driver and
 * their round, every single morning.
 */
function RootRedirect() {
  const { status, staff } = useAuth();

  if (status === 'loading') return <Spinner center label="Loading" />;
  if (status === 'anonymous' || staff === null) return <Navigate to="/login" replace />;
  return <Navigate to={homePathForRole(staff.role)} replace />;
}

function NotFound() {
  const { staff } = useAuth();
  const home = staff === null ? '/login' : homePathForRole(staff.role);

  return (
    <EmptyState
      title="Page not found"
      description="That address does not exist in the back office."
      action={
        <Link to={home}>
          <Button variant="secondary">Back to the start</Button>
        </Link>
      }
    />
  );
}

/**
 * The last line before a white screen.
 *
 * A render error in one interface must not take the browser tab down with it —
 * a driver mid-round needs a reload button, not an empty page and no idea why.
 * Errors from the API are not caught here: those are values, handled by the
 * screens that asked for them.
 */
interface BoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is the only channel there is until a reporter is wired up.
    console.error('Back office crashed', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <EmptyState
          tone="danger"
          title="Something broke"
          description={this.state.error.message}
          action={
            <Button
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}

function AppRoutes() {
  return (
    <Suspense fallback={<Spinner center label="Loading" />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<RootRedirect />} />

        {/* The office. A driver token here is redirected to /driver, and the
            server refuses every /admin/* call from one regardless. */}
        <Route element={<RequireRole role="admin" />}>
          <Route path="/admin/*" element={<AdminApp />} />
        </Route>

        {/* No role: the driver API is requireStaff(), not requireStaff('driver'),
            so the office can cover a round when a handset dies. */}
        <Route element={<RequireRole />}>
          <Route path="/driver/*" element={<DriverApp />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export function AppShell() {
  // Created once per mount rather than at module scope: a client shared across
  // hot reloads keeps one signed-out user's cached rows alive for the next.
  const [queryClient] = useState(createQueryClient);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/* AuthProvider is inside the router because RequireRole reads the
              location, and inside the query client because signing out clears
              the cache. */}
          <AuthProvider>
            <ToastProvider>
              <AppRoutes />
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
